import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';
import { useSEO, SITE_URL } from '../../hooks/useSEO';

const AwarenessTrainingPage = () => {
  useSEO({
    title: 'Security Awareness Training for Mortgage Employees | Mortgage Moat',
    description: 'Engaging cybersecurity awareness training that stops employees from clicking phishing emails. Mortgage-specific modules, GLBA compliance tracking, and 95% completion rate.',
    keywords: 'security awareness training, employee cybersecurity training, phishing prevention, GLBA training, mortgage security education',
    canonical: '/awareness-training',
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'Service',
        name: 'Security Awareness Training',
        provider: { '@type': 'Organization', name: 'Mortgage Moat', url: SITE_URL },
        url: `${SITE_URL}/awareness-training`,
        description: 'Interactive security awareness training for mortgage company employees covering phishing, GLBA compliance, and real-world threat scenarios.',
        areaServed: { '@type': 'Country', name: 'United States' },
        serviceType: 'Security Awareness Training',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
          { '@type': 'ListItem', position: 2, name: 'Awareness Training', item: `${SITE_URL}/awareness-training` },
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: 'How long does each security awareness training module take?',
            acceptedAnswer: { '@type': 'Answer', text: 'Most modules take 20-30 minutes to complete. We break content into bite-sized lessons so employees can train during downtime without disrupting their workday.' },
          },
          {
            '@type': 'Question',
            name: 'Can employees complete security training on their phones?',
            acceptedAnswer: { '@type': 'Answer', text: 'Yes! Our platform is fully mobile-responsive. Employees can train on any device – desktop, tablet, or smartphone.' },
          },
          {
            '@type': 'Question',
            name: 'Is the security training specific to mortgage companies?',
            acceptedAnswer: { '@type': 'Answer', text: 'Yes. While we cover universal security topics, our content includes mortgage-specific scenarios, GLBA compliance requirements, and threats commonly targeting the mortgage industry.' },
          },
        ],
      },
    ],
  });

  const features = [
    {
      icon: 'ri-graduation-cap-line',
      title: 'Interactive Training Modules',
      description: 'Engaging, bite-sized lessons that employees actually complete – no boring hour-long videos'
    },
    {
      icon: 'ri-shield-user-line',
      title: 'Real-World Scenarios',
      description: 'Training based on actual threats targeting mortgage companies, not generic content'
    },
    {
      icon: 'ri-trophy-line',
      title: 'Gamification & Rewards',
      description: 'Points, badges, and leaderboards that make security training competitive and fun'
    },
    {
      icon: 'ri-file-list-3-line',
      title: 'Compliance Tracking',
      description: 'Automatic documentation of training completion for regulatory requirements'
    },
    {
      icon: 'ri-bar-chart-box-line',
      title: 'Progress Analytics',
      description: 'Detailed reporting on employee performance, knowledge gaps, and improvement trends'
    },
    {
      icon: 'ri-smartphone-line',
      title: 'Mobile-Friendly',
      description: 'Train anywhere, anytime on any device – perfect for remote and hybrid teams'
    }
  ];

  const topics = [
    {
      category: 'Core Security Topics',
      modules: [
        'Phishing & Email Security',
        'Password Security & MFA',
        'Social Engineering Tactics',
        'Ransomware Prevention',
        'Data Protection & Privacy',
        'Mobile Device Security'
      ]
    },
    {
      category: 'Mortgage Industry Specific',
      modules: [
        'GLBA Compliance Requirements',
        'Customer Data Protection',
        'Wire Fraud Prevention',
        'Vendor Security Risks',
        'Remote Work Security',
        'Incident Reporting Procedures'
      ]
    },
    {
      category: 'Advanced Topics',
      modules: [
        'Cloud Security Best Practices',
        'Secure File Sharing',
        'Business Email Compromise',
        'Physical Security Awareness',
        'Insider Threat Detection',
        'Security Policy Understanding'
      ]
    }
  ];

  const benefits = [
    {
      stat: '90%',
      label: 'Reduction in successful phishing attacks after training'
    },
    {
      stat: '95%',
      label: 'Employee completion rate with our engaging content'
    },
    {
      stat: '30 min',
      label: 'Average time to complete each training module'
    },
    {
      stat: '100%',
      label: 'Compliance documentation automatically generated'
    }
  ];

  const process = [
    {
      step: '1',
      title: 'Enroll Your Team',
      description: 'Add employees to the platform in minutes via CSV upload or manual entry'
    },
    {
      step: '2',
      title: 'Customize Training Path',
      description: 'Choose which modules to assign and set completion deadlines'
    },
    {
      step: '3',
      title: 'Employees Complete Training',
      description: 'Staff complete interactive modules at their own pace on any device'
    },
    {
      step: '4',
      title: 'Track Progress',
      description: 'Monitor completion rates, quiz scores, and identify who needs help'
    },
    {
      step: '5',
      title: 'Receive Certificates',
      description: 'Employees earn certificates upon completion for compliance documentation'
    },
    {
      step: '6',
      title: 'Ongoing Reinforcement',
      description: 'Regular refresher training and updates on emerging threats'
    }
  ];

  return (
    <div className="min-h-screen">
      <Header />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-red-600 to-red-800 text-white py-24">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative max-w-7xl mx-auto px-6">
          <div className="max-w-3xl">
            <div className="inline-block bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-bold mb-6">
              SECURITY AWARENESS TRAINING
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Stop Employees From Clicking Phishing Emails
            </h1>
            <p className="text-xl mb-8 text-white/90">
              Engaging security training that employees actually complete – protecting your mortgage business from the inside out.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="/purchase/phishing-simulation"
                className="inline-flex items-center justify-center gap-2 bg-cyan-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-cyan-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                Get Started
                <i className="ri-arrow-right-line"></i>
              </a>
              <a 
                href="/schedule-call"
                className="inline-flex items-center justify-center gap-2 bg-white text-cyan-600 px-8 py-4 rounded-lg font-semibold hover:bg-cyan-50 border-2 border-cyan-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                Start Testing Your Team
                <i className="ri-team-line"></i>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="text-center">
                <div className="text-5xl font-bold text-red-600 mb-2">{benefit.stat}</div>
                <p className="text-gray-600 font-semibold">{benefit.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Training Matters */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Your Employees Are Your First Line of Defense
              </h2>
              <p className="text-xl text-gray-600 mb-6">
                95% of cybersecurity breaches are caused by human error. Even the best security technology can't protect you if your employees click on phishing emails or use weak passwords.
              </p>
              <p className="text-lg text-gray-600 mb-8">
                Our security awareness training transforms your team from your biggest vulnerability into your strongest asset. With engaging, mortgage-specific content, we make security training something employees actually want to complete.
              </p>
              <ul className="space-y-3">
                {[
                  'Reduce successful phishing attacks by 90%',
                  'Meet GLBA and FFIEC training requirements',
                  'Build a security-conscious company culture',
                  'Protect customer data and company reputation',
                  'Lower cyber insurance premiums',
                  'Empower employees to spot and report threats'
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                      <i className="ri-check-line text-green-500 text-xl"></i>
                    </div>
                    <span className="text-gray-700 font-semibold">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="relative h-96 lg:h-full min-h-[500px]">
              <img 
                src="https://static.readdy.ai/image/807f7a13fa3a4707437af02957aa93c9/6ecef7f357511803c377b23aee432709.png"
                alt="Team Training"
                className="w-full h-full object-cover object-top rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Training That Actually Works
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Forget boring PowerPoints and hour-long videos. Our platform delivers engaging, effective training that employees complete.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="bg-gray-50 p-8 rounded-xl hover:shadow-lg transition-shadow">
                <div className="w-16 h-16 flex items-center justify-center bg-red-100 rounded-xl mb-6">
                  <i className={`${feature.icon} text-3xl text-red-600`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Training Topics */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Comprehensive Training Curriculum
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              18+ training modules covering everything your mortgage team needs to know about cybersecurity.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {topics.map((topic, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-6 pb-4 border-b-2 border-red-600">
                  {topic.category}
                </h3>
                <ul className="space-y-3">
                  {topic.modules.map((module, moduleIndex) => (
                    <li key={moduleIndex} className="flex items-start gap-3">
                      <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                        <i className="ri-play-circle-line text-red-600 text-xl"></i>
                      </div>
                      <span className="text-gray-700">{module}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              How It Works
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Get your team trained in just a few simple steps.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {process.map((item, index) => (
              <div key={index} className="relative">
                <div className="flex flex-col items-center text-center">
                  <div className="w-20 h-20 flex items-center justify-center flex-shrink-0 bg-red-600 text-white text-3xl font-bold rounded-full mb-6">
                    {item.step}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{item.title}</h3>
                  <p className="text-gray-600">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Customized Training Solutions
            </h2>
            <p className="text-xl text-gray-600">
              Every mortgage company is unique. Let's discuss a training plan tailored to your needs.
            </p>
          </div>

          <div className="max-w-2xl mx-auto">
            {/* Custom Training Plan */}
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
              <div className="bg-gray-100 text-center py-6">
                <div className="text-sm font-bold uppercase tracking-wide text-gray-600 mb-2">Custom Training Plan</div>
                <div className="text-3xl font-bold text-gray-900 mb-2">Consultation Required</div>
                <div className="text-gray-600">Pricing customized to your team size and needs</div>
              </div>
              
              <div className="p-8">
                <ul className="space-y-4 mb-8">
                  {[
                    'Access to all 18+ training modules',
                    'Interactive lessons & quizzes',
                    'Completion certificates',
                    'Progress tracking dashboard',
                    'Email support',
                    'Monthly content updates',
                    'Compliance documentation',
                    'Flexible scheduling options',
                    'Custom module development available',
                    'Volume discounts for larger teams'
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                        <i className="ri-check-line text-green-500 text-xl"></i>
                      </div>
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>

                <Button 
                  variant="primary" 
                  size="lg" 
                  className="w-full py-4 font-bold whitespace-nowrap"
                  onClick={() => window.REACT_APP_NAVIGATE('/portal/contact')}
                >
                  Schedule Consultation
                </Button>
                
                <p className="text-sm text-gray-600 text-center mt-4">
                  We'll discuss your team size, training goals, and create a custom plan that fits your budget.
                </p>
              </div>
            </div>
          </div>

          <div className="mt-12 text-center">
            <p className="text-gray-600">
              <strong>Questions about training options?</strong> Our team is here to help you find the perfect solution.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">
            Frequently Asked Questions
          </h2>
          
          <div className="space-y-6">
            {[
              {
                question: 'How long does each training module take?',
                answer: 'Most modules take 20-30 minutes to complete. We break content into bite-sized lessons so employees can train during downtime without disrupting their workday.'
              },
              {
                question: 'Can employees complete training on their phones?',
                answer: 'Yes! Our platform is fully mobile-responsive. Employees can train on any device – desktop, tablet, or smartphone.'
              },
              {
                question: 'How do we track who has completed training?',
                answer: 'Your admin dashboard shows real-time completion status for all employees, including quiz scores, time spent, and certificates earned. You can export reports for compliance documentation.'
              },
              {
                question: 'Is the training specific to mortgage companies?',
                answer: 'Yes. While we cover universal security topics, our content includes mortgage-specific scenarios, GLBA compliance requirements, and threats commonly targeting the mortgage industry.'
              },
              {
                question: 'What happens if an employee fails a quiz?',
                answer: 'Employees can retake quizzes as many times as needed. We also provide additional resources and explanations to help them understand the material before retrying.'
              },
              {
                question: 'Can we customize the training content?',
                answer: 'Annual plan subscribers can request custom modules tailored to their specific policies, procedures, and security requirements. Contact us to discuss customization options.'
              }
            ].map((faq, index) => (
              <div key={index} className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3">{faq.question}</h3>
                <p className="text-gray-600">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-br from-red-600 to-red-800 text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to Build Your Human Firewall?
          </h2>
          <p className="text-xl mb-8 text-white/90">
            Start training your team today and transform them into your strongest security defense.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="/purchase/phishing-simulation"
              className="inline-flex items-center justify-center gap-2 bg-cyan-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-cyan-700 transition-colors whitespace-nowrap cursor-pointer"
            >
              Start Training Now
              <i className="ri-arrow-right-line"></i>
            </a>
            <a 
              href="/schedule-call"
              className="inline-flex items-center justify-center gap-2 bg-white text-cyan-600 px-8 py-4 rounded-lg font-semibold hover:bg-cyan-50 border-2 border-cyan-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              Schedule a Demo
              <i className="ri-calendar-line"></i>
            </a>
          </div>
          <p className="text-sm text-white/75 mt-6">
            Questions? Call us at 844-662-8277 or email sales@mortgagemoat.com
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AwarenessTrainingPage;